/**
 * 工作流编辑器包（@datamanus/workflow-editor）的宿主装配。
 *
 * 包内代码不直接依赖 web-app 的任何模块，全部能力都从这里注入。
 * 注意：每个字段都用箭头函数惰性转发，不要在模块顶层解构宿主模块的导出——
 * 这样测试里 vi.mock('@/utils') / vi.mock('@/api/index') 等替身在调用时刻仍能生效。
 */
import { computed } from 'vue'
import { setWorkflowEditorHost, type WorkflowEditorHost } from '@datamanus/workflow-editor/src/host'
import apiClient from '@/api'
import { fetchModelCatalog } from '@/api/modelConfig'
import { mcpToolProviderApi } from '@/api/mcpToolProvider'
import { getFileDownloadUrl, uploadFile } from '@/api/fileManager'
import { useModelCatalog } from '@/composables/useModelCatalog'
import { useKnowledgeBaseStore } from '@/stores/knowledgeBase'
import type { KnowledgeBaseQueryParams } from '@/types/knowledge-base'
import { SSEClient } from '@/transport/sse-client'
import { generateUUID, getProjectId, logger, tokenHolder } from '@/utils'
import { formatShanghaiDateTime } from '@/utils/dateTime'
import { getErrorMessage } from '@/utils/get-error-message'
import { loadLocalMonaco } from '@/utils/monacoLoader'

export const workflowEditorHost: WorkflowEditorHost = {
  // 用 rest 参数原样转发，保持调用元数（测试里 toHaveBeenCalledWith 对参数个数敏感）
  http: {
    get: (...args) => apiClient.get(...args),
    post: (...args) => apiClient.post(...args),
    put: (...args) => apiClient.put(...args),
    delete: (...args) => apiClient.delete(...args),
    patch: (...args) => apiClient.patch(...args),
  },
  createSseClient: (options) => new SSEClient(options),
  auth: {
    hasAccessToken: () => tokenHolder.hasAccessToken(),
    getAccessToken: () => tokenHolder.getAccessToken(),
    getProjectId: () => getProjectId(),
  },
  logger: {
    debug: (...args) => logger.debug(...args),
    info: (...args) => logger.info(...args),
    warn: (...args) => logger.warn(...args),
    error: (...args) => logger.error(...args),
  },
  generateUUID: () => generateUUID(),
  formatDateTime: (value) => formatShanghaiDateTime(value),
  getErrorMessage: (error, fallback) => getErrorMessage(error, fallback),
  loadMonaco: () => loadLocalMonaco(),
  models: {
    fetchCatalog: (params) => fetchModelCatalog(params),
    useCatalog: (autoLoad) => useModelCatalog(autoLoad),
  },
  mcp: {
    listProviders: () => mcpToolProviderApi.list(),
  },
  files: {
    upload: (file, params) => uploadFile(file, params),
    getDownloadUrl: (fileId) => getFileDownloadUrl(fileId),
  },
  knowledgeBases: {
    use: () => {
      const store = useKnowledgeBaseStore()
      return {
        knowledgeBases: computed(() => store.knowledgeBases),
        groups: computed(() => store.groups),
        load: async (params) => {
          // 包侧只声明 status 为字符串；宿主 store 的枚举更窄，由调用方保证取值合法
          await store.loadKnowledgeBases(params as Partial<KnowledgeBaseQueryParams>)
        },
      }
    },
  },
}

/** 应用启动时调用一次。 */
export function installWorkflowEditorHost(): void {
  setWorkflowEditorHost(workflowEditorHost)
}
